import {
  IMethod,
  IMethodTableColumns,
  INewMethodQuery,
  useCreateMethodMutation,
  useEditMethodMutation,
} from '@entities/method';
import { ModalEditEntity } from '@features/modal-entity-edit';
import { ModalNewEntity } from '@features/modal-entity-new';
import { ModalViewEntity } from '@features/modal-entity-view';
import { ITableActionsConfig, TableActionBtns } from '@features/table-action-btns';
import RequestStatusLabel from '@shared/components/RequestStatusLabel';

interface IProps {
  refetch?: () => void;
}

export const useTableSettings = ({ refetch }: IProps) => {
  // THead settings
  const columnTitles: IMethodTableColumns = {
    name: 'Name',
    type: 'Type',
    created_at: 'Created at',
    updated_at: 'Updated at',
    status: 'Status',
    actions: 'Actions',
  };

  // TBody settings
  const renderRow = (key: keyof IMethodTableColumns, method: IMethod) => {
    switch (key) {
      case 'status':
        return <RequestStatusLabel status='active' />;

      case 'actions': {
        const excludeEditKeys: (keyof IMethod)[] = [
          'id',
          'created_at',
          'updated_at',
          'deleted_at',
          'logo',
          'logo_url',
          'type',
          'currencies',
        ];

        const excludeViewKeys: (keyof IMethod)[] = ['deleted_at', 'logo', 'logo_url'];

        const actionBtnsConfig: ITableActionsConfig = {
          edit: {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <ModalEditEntity
                title={`Edit Method ${method.name}`}
                entity={{
                  ...method,
                  method_type: method.type || '',
                }}
                excludeKeys={excludeEditKeys}
                useEditEntityMutation={useEditMethodMutation}
                refetch={refetch}
                omitUnchangedValues={true}
              />
            ),
            popupSize: 'sm',
          },
          view: {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <ModalViewEntity
                excludeKeys={excludeViewKeys}
                entity={method}
                title={`View Method ${method.name}`}
              />
            ),
            popupSize: 'md',
          },
        };

        return <TableActionBtns config={actionBtnsConfig} />;
      }

      default:
        return method[key];
    }
  };

  // New merchant settings
  const newMethodKeys: (keyof INewMethodQuery)[] = [
    'currencies',
    'name',
    'payment_link_placeholder',
    'method_type',
    'description',
  ];

  const onTransformFormData = (formData: FormData) => {
    const currencies: string[] = [];
    for (const [key, value] of formData.entries()) {
      if (key === 'currencies') {
        currencies.push(value as string);
      }
    }

    const newFormData = new FormData();

    for (const [key, value] of formData.entries()) {
      if (key !== 'currencies') {
        newFormData.append(key, value);
      }
    }

    newFormData.append('type', formData.get('method_type') as string);
    newFormData.delete('method_type');

    currencies.forEach(currencyId => {
      newFormData.append('currencies[]', currencyId);
    });

    return newFormData;
  };

  const NewMethod = () => (
    <ModalNewEntity
      title='New Method'
      entityKeys={newMethodKeys}
      useCreateEntityMutation={useCreateMethodMutation}
      onTransformFormData={onTransformFormData}
      refetch={refetch}
    />
  );

  return {
    columnTitles,
    renderRow,
    NewMethod,
  };
};
